<script setup lang="ts">
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { computed, onMounted, ref, watch } from 'vue'
  import {
    ClipboardDocumentIcon,
    ClipboardDocumentCheckIcon,
    XMarkIcon
  } from '@heroicons/vue/24/outline'
  import { useUserStore } from '@/shared/stores/userStore'
  import type { CustomerUser } from '@/types/generated'
  import { OrganisationsService } from '@/types/generated'

  const isCopied = ref(false)
  const { fetchUser } = useUserStore()
  const user = computed<CustomerUser>(() => useUserStore().user)
  const newBlockedEmail = ref('')

  const copyEmail = () => {
    navigator.clipboard.writeText(user.value!.org.inbound_email_address).then(() => {
      isCopied.value = true
      setTimeout(() => {
        isCopied.value = false
      }, 2000)
    })
  }

  const addBlockedEmail = async () => {
    if (!newBlockedEmail.value || !isValidEmail(newBlockedEmail.value)) return
    user.value!.org.email_blocked_senders.push(newBlockedEmail.value)
    newBlockedEmail.value = ''
    await updateOrganisation({
      email_blocked_senders: user.value!.org.email_blocked_senders
    })
  }

  const removeBlockedEmail = async (index: number) => {
    user.value!.org.email_blocked_senders.splice(index, 1)
    await updateOrganisation({
      email_blocked_senders: user.value!.org.email_blocked_senders
    })
  }

  const updateOrganisation = async (data: any) => {
    await OrganisationsService.organisationsPartialUpdate({
      uuid: user.value!.org.uuid,
      requestBody: data
    })
  }

  const toggleSpamFilter = async () => {
    user.value!.org.email_spam_filter_active = !user.value!.org.email_spam_filter_active
    await updateOrganisation({
      email_spam_filter_active: user.value!.org.email_spam_filter_active
    })
  }

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  onMounted(() => {
    if (!user.value) {
      fetchUser()
    }
  })

  const { t } = useTranslation({
    en: {
      description: 'Use this email address to forward inquiries directly to JUPUS.',
      blockedSenders: 'Blocked Senders',
      blockedSendersDescription:
        'Add email addresses that should be blocked from sending inquiries to JUPUS.',
      addEmail: 'Add Email',
      invalidEmail: 'Please enter a valid email address',
      noblockedSenders: 'No blocked email addresses',
      enterEmailPlaceholder: 'Enter email address to block',
      spamFilter: 'Spam Filter',
      spamFilterDescription: 'Automatically filter out spam emails'
    },
    de: {
      description: 'Nutzen Sie diese E-Mail-Adresse um Anfragen direkt an JUPUS weiterzuleiten.',
      blockedSenders: 'Blockierte Absender',
      blockedSendersDescription:
        'Fügen Sie hier E-Mail-Adressen hinzu, die geblockt werden sollen.',
      addEmail: 'E-Mail hinzufügen',
      invalidEmail: 'Bitte geben Sie eine gültige E-Mail-Adresse ein',
      noblockedSenders: 'Keine blockierten E-Mail-Adressen',
      enterEmailPlaceholder: 'E-Mail-Adresse zum Blockieren eingeben',
      spamFilter: 'Spam-Filter',
      spamFilterDescription: 'Spam-E-Mails automatisch herausfiltern'
    },
    fr: {
      description:
        'Utilisez cette adresse e-mail pour transférer des demandes directement à JUPUS.',
      blockedSenders: 'Expéditeurs bloqués',
      blockedSendersDescription:
        'Ajoutez les adresses e-mail qui doivent être empêchées d’envoyer des demandes à JUPUS.',
      addEmail: 'Ajouter une adresse e-mail',
      invalidEmail: 'Veuillez saisir une adresse e-mail valide',
      noblockedSenders: 'Aucune adresse e-mail bloquée',
      enterEmailPlaceholder: 'Saisir l’adresse e-mail à bloquer',
      spamFilter: 'Filtre anti-spam',
      spamFilterDescription: 'Filtrer automatiquement les e-mails indésirables'
    }
  })
</script>

<template>
  <SettingsPage v-if="user">
    <div class="space-y-8">
      <!-- Existing JUPUS Email Address Section -->
      <div class="mb-6">
        <div class="">
          <div class="mb-3 block font-medium">JUPUS E-Mail Adresse</div>
          <p class="mb-4 text-sm text-gray-700">
            Ihre JUPUS E-Mail Adresse ermöglicht es Ihnen, E-Mails direkt an JUPUS weiterzuleiten,
            wo unsere leistungsstarke KI automatisiert eine neue Anfrage aus der E-Mail erstellt.
            Dabei wird nicht nur der Inhalt der E-Mail ausgewertet, sondern auch relevante Dokumente
            automatisch angehängt, der Kontext der Anfrage erkannt und die Stammdaten des Mandanten
            erstellt.
          </p>
          <div class="relative">
            <input
              type="text"
              :value="user.org.inbound_email_address"
              readonly
              class="w-full rounded-md border-gray-200 px-3 py-2 pr-32 text-gray-800 transition-all duration-300
                focus:bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            <button
              class="absolute bottom-1 right-1 top-1 flex min-w-[100px] items-center justify-center rounded-md
                bg-purple-600 px-4 font-medium text-white transition-all duration-300 hover:bg-purple-700
                focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
              @click="copyEmail"
            >
              <span
                v-if="!isCopied"
                class="flex items-center"
              >
                <ClipboardDocumentIcon class="mr-2 h-4 w-4" />
                Kopieren
              </span>
              <span
                v-else
                class="flex items-center"
              >
                <ClipboardDocumentCheckIcon class="mr-2 h-4 w-4" />
                Kopiert!
              </span>
            </button>
          </div>
        </div>
      </div>

      <!-- Spam Filter Section -->
      <div class="border-b border-gray-200 pb-6">
        <div class="mb-3 block font-medium">{{ t('spamFilter') }}</div>
        <div class="flex items-start">
          <div class="flex h-6 items-center">
            <input
              type="checkbox"
              :checked="user.org.email_spam_filter_active"
              class="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              @change="toggleSpamFilter"
            />
          </div>
          <div class="ml-3">
            <p class="text-sm text-gray-700">{{ t('spamFilterDescription') }}</p>
          </div>
        </div>
      </div>

      <!-- New Blocked Senders Section -->
      <div>
        <div class="mb-3 block font-medium">{{ t('blockedSenders') }}</div>
        <p class="mb-4 text-sm text-gray-700">
          {{ t('blockedSendersDescription') }}
        </p>

        <!-- Add Email Form -->
        <div class="mb-4">
          <div class="relative">
            <input
              v-model="newBlockedEmail"
              type="email"
              :placeholder="t('enterEmailPlaceholder')"
              class="w-full rounded-md border-gray-200 px-3 py-2 pr-32 text-gray-800 transition-all duration-300
                focus:bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              @keyup.enter="addBlockedEmail"
            />
            <button
              :disabled="!newBlockedEmail || !isValidEmail(newBlockedEmail)"
              class="absolute bottom-1 right-1 top-1 flex min-w-[140px] items-center justify-center rounded-md
                bg-purple-600 px-4 font-medium text-white transition-all duration-300 hover:bg-purple-700
                focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50"
              @click="addBlockedEmail"
            >
              {{ t('addEmail') }}
            </button>
          </div>
        </div>

        <!-- Blocked Emails List -->
        <div class="space-y-2">
          <div
            v-if="user.org.email_blocked_senders.length === 0"
            class="text-sm text-gray-500"
          >
            {{ t('noblockedSenders') }}
          </div>
          <div
            v-for="(email, index) in user.org.email_blocked_senders"
            :key="index"
            class="flex items-center justify-between rounded-md border border-gray-200 bg-white px-4 py-2"
          >
            <span class="text-gray-900">{{ email }}</span>
            <button
              class="ml-2 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              :title="'Remove ' + email"
              @click="removeBlockedEmail(index)"
            >
              <XMarkIcon class="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </SettingsPage>
</template>

<style scoped>
  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }

  .animate-pulse {
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }
</style>
